import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User } from "lucide-react";
import Reveal from "./Reveal";

const PROOF_MAIN =
  "https://customer-assets.emergentagent.com/job_handwerk-sales-agent/artifacts/n90d8ozd_proof_element_anonym.png";
const GALLERY = [
  {
    url: "https://customer-assets.emergentagent.com/job_handwerk-sales-agent/artifacts/cwamqe4g_backend_anfragen.png",
    caption: "Eingehende Anfragen im Backend",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_handwerk-sales-agent/artifacts/bope5n9r_ba_media_assistent.png",
    caption: "BA Media Assistent im Einsatz",
  },
];

// Echte WhatsApp-Belege (anonymisiert)
const WA = {
  kundenstimme:
    "https://customer-assets.emergentagent.com/job_handwerk-sales-agent/artifacts/4w3rfvbs_21.png",
  anfrage:
    "https://customer-assets.emergentagent.com/job_handwerk-sales-agent/artifacts/a5hjd67v_11.png",
};

export default function SocialProof() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section className="border-b border-white/5 px-6 py-14 md:py-16" data-testid="social-proof">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <p className="eyebrow mb-3">Echtes Ergebnis · kein Theorie-Versprechen</p>
          <h2 className="font-display max-w-2xl text-3xl font-bold text-white md:text-4xl">
            Echte Ergebnisse, kein Theorie-Versprechen.
          </h2>
        </Reveal>

        {/* Haupt-Proof-Element */}
        <Reveal delay={0.05}>
          <div className="mt-8 overflow-hidden rounded-xl border border-white/10 bg-[#14161B]">
            <img
              src={PROOF_MAIN}
              alt="Praxisbeispiel: Aus Werbebudget zu planbaren Anfragen, erste 4 Wochen (29.05.–25.06.2026)"
              className="w-full"
              loading="lazy"
            />
          </div>
        </Reveal>

        {/* Beleg-Galerie */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {GALLERY.map((g, i) => (
            <Reveal key={g.caption} delay={0.05 * i}>
              <button
                data-testid={`proof-gallery-${i}`}
                onClick={() => setLightbox(g.url)}
                className="group block w-full overflow-hidden rounded-lg border border-white/10 bg-[#14161B] text-left"
              >
                <img src={g.url} alt={g.caption} className="w-full transition-transform group-hover:scale-[1.02]" loading="lazy" />
                <span className="block px-4 py-3 text-xs text-gray-400">{g.caption}</span>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Echte WhatsApp-Belege */}
        <div className="mt-12">
          <Reveal>
            <h3 className="font-display text-xl text-white">Aus echten WhatsApp-Verläufen</h3>
            <p className="mb-6 mt-1 text-sm text-gray-500">
              Original-Nachrichten von Kunden und Anfragen über den Funnel.
            </p>
          </Reveal>
          <div className="grid items-start gap-6 lg:grid-cols-5">
            <Reveal className="lg:col-span-3" delay={0.05}>
              <figure className="group">
                <button
                  data-testid="wa-kundenstimme"
                  onClick={() => setLightbox(WA.kundenstimme)}
                  className="block w-full -rotate-[1.5deg] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0d10] shadow-2xl shadow-black/60 ring-1 ring-black/40 transition-transform duration-300 hover:rotate-0"
                >
                  <img
                    src={WA.kundenstimme}
                    alt="Echte WhatsApp-Kundenstimme, anonymisiert"
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover object-top"
                  />
                </button>
                <figcaption className="mt-3 text-xs text-gray-500">Echte Nachricht · anonymisiert</figcaption>
              </figure>
            </Reveal>
            <Reveal className="lg:col-span-2" delay={0.14}>
              <figure className="group">
                <button
                  data-testid="wa-anfrage"
                  onClick={() => setLightbox(WA.anfrage)}
                  className="block w-full rotate-[1.5deg] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0d10] shadow-2xl shadow-black/60 ring-1 ring-black/40 transition-transform duration-300 hover:rotate-0"
                >
                  <img
                    src={WA.anfrage}
                    alt="Echte WhatsApp-Anfrage über den Funnel, anonymisiert"
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover object-top"
                  />
                </button>
                <figcaption className="mt-3 text-xs text-gray-500">Echte Nachricht · anonymisiert</figcaption>
              </figure>
            </Reveal>
          </div>

          {/* Gründer */}
          <Reveal delay={0.1}>
            <div className="mt-6 flex flex-col gap-4 rounded-lg border border-[#3DDC97]/20 bg-[#3DDC97]/5 p-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-2xl text-sm leading-relaxed text-gray-200">
                „Wir haben selbst Betriebe aufgebaut und skaliert – deshalb wissen wir, was
                für Handwerk und Bau wirklich funktioniert."
              </p>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1F222A] text-gray-500">
                  <User size={18} />
                </div>
                <div className="text-xs">
                  <p className="font-semibold text-white">Arber Bellegu</p>
                  <p className="text-gray-500">Gründer · BA Media Performance</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <p className="mt-6 text-xs leading-relaxed text-gray-600">
            Reale Kampagnendaten, Zeitraum erste 4 Wochen (29.05.–25.06.2026). „Anfragen" =
            über Funnel &amp; BA Media Assistent qualifizierte Kontakte. „Aufträge" = tatsächlich
            beauftragte Projekte. Kundennamen aus Datenschutzgründen anonymisiert. Ergebnisse
            variieren je nach Dienstleistung, Region und Kapazität.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            data-testid="proof-lightbox"
          >
            <button className="absolute right-5 top-5 text-white" onClick={() => setLightbox(null)} aria-label="Schließen">
              <X size={28} />
            </button>
            <img src={lightbox} alt="" className="max-h-[90vh] max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
